import { useCallback, useEffect, useRef, useState } from "react";
import {
  assertPasskeyStepUp,
  bindStepUpProof,
  isStepUpRequired,
  isStepUpUnavailable,
  StepUpAbandonedError,
  totpStepUp,
  type StepUpProof,
} from "../api/stepup.api";
import {
  cancelAllCeremonies,
  cancelCeremonies,
  isCeremonyActive,
  type CeremonyOwner,
} from "@/lib/webauthn";
import { isStaleSessionError } from "@/lib/api/client";

export type StepUpPromptFollows = () => boolean;
export type StepUpPerform = (
  stepUp: StepUpProof | undefined,
  stepUpPromptFollows: StepUpPromptFollows,
) => Promise<void>;
export type StepUpError = "required" | "unavailable" | "ceremony" | "stale-session" | null;

export interface StepUpRunOptions {
  needsProof?: boolean;
  dashboardOnly?: boolean;
  onDismiss?: () => void;
}

interface Attempt {
  token: number;
  fn: StepUpPerform;
  onDismiss?: () => void;
}

export function useStepUp(ceremonyOwner: CeremonyOwner) {
  const [openAttempt, setOpenAttempt] = useState<{
    dashboardOnly: boolean;
    serverDemandedProof: boolean;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<StepUpError>(null);
  const [proofOnWire, setProofOnWire] = useState(false);
  const [dismissedRequired, setDismissedRequired] = useState(false);
  const [nonce, setNonce] = useState(0);
  const tokenRef = useRef(0);
  const attemptRef = useRef<Attempt | null>(null);
  const inFlightRef = useRef(new Map<number, (() => void) | undefined>());
  const forgottenRef = useRef(new Set<number>());
  const ceremonyRef = useRef<number | null>(null);
  const unmountedRef = useRef(false);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      attemptRef.current = null;
    };
  }, []);

  const isCurrent = useCallback(
    (token: number) => !unmountedRef.current && attemptRef.current?.token === token,
    [],
  );

  const isMounted = useCallback(() => !unmountedRef.current, []);

  const resetStillInUse = useCallback(
    (onDismiss: (() => void) | undefined) =>
      Array.from(inFlightRef.current.values()).includes(onDismiss),
    [],
  );

  const open = useCallback(
    (fn: StepUpPerform, options: StepUpRunOptions, serverDemandedProof: boolean) => {
      if (isCeremonyActive()) cancelAllCeremonies();
      tokenRef.current += 1;
      attemptRef.current = { token: tokenRef.current, fn, onDismiss: options.onDismiss };
      setBusy(false);
      setError(null);
      setProofOnWire(false);
      setOpenAttempt({ dashboardOnly: options.dashboardOnly ?? false, serverDemandedProof });
      setNonce((n) => n + 1);
    },
    [],
  );

  const run = useCallback(
    async (fn: StepUpPerform, options: StepUpRunOptions = {}) => {
      setDismissedRequired(false);
      if (options.needsProof) {
        open(fn, options, false);
        return;
      }
      try {
        await fn(undefined, isMounted);
      } catch (err) {
        if (isStepUpRequired(err) && isMounted()) {
          open(fn, options, true);
        }
      }
    },
    [open, isMounted],
  );

  const submit = useCallback(
    async (proof: StepUpProof, token: number) => {
      const attempt = attemptRef.current;
      if (!attempt || attempt.token !== token || unmountedRef.current) return;
      const inFlight = inFlightRef.current;
      if (inFlight.has(token)) return;
      inFlight.set(token, attempt.onDismiss);
      bindStepUpProof(
        proof,
        () => isCurrent(token),
        () => {
          if (isCurrent(token)) setProofOnWire(true);
        },
      );
      setBusy(true);
      setError(null);
      let failure: unknown = null;
      try {
        await attempt.fn(proof, () => isCurrent(token));
      } catch (err) {
        failure = err;
      }
      inFlight.delete(token);
      const forgotten = forgottenRef.current.delete(token);
      if (attemptRef.current?.token !== token) {
        if (unmountedRef.current) return;
        if (failure instanceof StepUpAbandonedError && !resetStillInUse(attempt.onDismiss)) {
          attempt.onDismiss?.();
        } else if (isStepUpRequired(failure) && !forgotten) {
          setDismissedRequired(true);
        }
        return;
      }
      setBusy(false);
      setProofOnWire(false);
      if (isStepUpRequired(failure)) {
        setError("required");
        return;
      }
      attemptRef.current = null;
      setOpenAttempt(null);
    },
    [isCurrent, resetStillInUse],
  );

  const withPasskey = useCallback(async () => {
    const attempt = attemptRef.current;
    if (!attempt) return;
    const { token } = attempt;
    if (ceremonyRef.current === token || inFlightRef.current.has(token)) return;
    setBusy(true);
    setError(null);
    ceremonyRef.current = token;
    let proof: StepUpProof;
    try {
      proof = await assertPasskeyStepUp(ceremonyOwner, () => isCurrent(token));
    } catch (err) {
      if (isCurrent(token)) {
        setBusy(false);
        setError(
          isStaleSessionError(err)
            ? "stale-session"
            : isStepUpUnavailable(err)
              ? "unavailable"
              : "ceremony",
        );
      }
      return;
    } finally {
      if (ceremonyRef.current === token) ceremonyRef.current = null;
    }
    if (!isCurrent(token)) return;
    await submit(proof, token);
  }, [ceremonyOwner, isCurrent, submit]);

  const withTotp = useCallback(
    (code: string) => submit(totpStepUp(code), attemptRef.current?.token ?? -1),
    [submit],
  );

  const cancel = useCallback(() => {
    const attempt = attemptRef.current;
    if (!attempt) {
      setOpenAttempt(null);
      setError(null);
      setBusy(false);
      setProofOnWire(false);
      setDismissedRequired(false);
      return;
    }
    cancelCeremonies(ceremonyOwner);
    attemptRef.current = null;
    setOpenAttempt(null);
    setError(null);
    setBusy(false);
    setProofOnWire(false);
    if (!resetStillInUse(attempt.onDismiss)) attempt.onDismiss?.();
  }, [ceremonyOwner, resetStillInUse]);

  const forgetInFlight = useCallback(() => {
    for (const token of inFlightRef.current.keys()) forgottenRef.current.add(token);
    setDismissedRequired(false);
  }, []);

  const acknowledgeDismissed = useCallback(() => setDismissedRequired(false), []);

  return {
    open: openAttempt !== null,
    dashboardOnly: openAttempt?.dashboardOnly ?? false,
    serverDemandedProof: openAttempt?.serverDemandedProof ?? false,
    busy,
    error,
    proofOnWire,
    dismissedRequired: dismissedRequired && openAttempt === null,
    nonce,
    run,
    withPasskey,
    withTotp,
    cancel,
    forgetInFlight,
    acknowledgeDismissed,
  };
}
